import {
  BOARD_SIZE,
  boardFromValues,
  composeBoard,
  conflictingCells,
  isComplete,
  peerIndexes,
  type Board,
  type CellValue,
} from './board';
import type { Difficulty } from './generator';
import { countSolutions } from './solver';

export type SessionStatus = 'playing' | 'paused' | 'completed' | 'failed';

export const MAX_MISTAKES = 3;

type HistorySnapshot = {
  entries: Board;
  candidates: readonly number[];
};

export type GameSession = {
  puzzle: Board;
  entries: Board;
  candidates: readonly number[];
  selectedIndex: number;
  difficulty: Difficulty;
  status: SessionStatus;
  elapsedMs: number;
  startedAtMs: number | null;
  automaticallyPaused: boolean;
  conflicts: ReadonlySet<number>;
  incorrectCells: ReadonlySet<number>;
  mistakes: number;
  maxMistakes: number;
  history: readonly HistorySnapshot[];
  future: readonly HistorySnapshot[];
  canUndo: boolean;
  canRedo: boolean;
};

export type SessionAction =
  | { type: 'select'; index: number }
  | { type: 'enterValue'; value: CellValue; nowMs?: number }
  | { type: 'toggleCandidate'; value: CellValue }
  | { type: 'clear' }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'pause'; nowMs: number; automatic: boolean }
  | { type: 'resume'; nowMs: number }
  | { type: 'resumeAutomatic'; nowMs: number }
  | { type: 'restart'; nowMs: number };

function snapshot(state: GameSession): HistorySnapshot {
  return { entries: state.entries, candidates: state.candidates };
}

function boardWithEntry(puzzle: Board, index: number, value: CellValue): Board {
  const values = [...puzzle];
  values[index] = value;
  return boardFromValues(values);
}

function hasAnyEntry(entries: Board): boolean {
  return entries.some((entry) => entry !== 0);
}

function incorrectCells(puzzle: Board, entries: Board): ReadonlySet<number> {
  if (!hasAnyEntry(entries)) {
    return new Set();
  }
  const cells = new Set<number>();
  for (let index = 0; index < BOARD_SIZE; index += 1) {
    const entry = entries[index] ?? 0;
    if (puzzle[index] === 0 && entry !== 0 && isMistake(puzzle, index, entry)) {
      cells.add(index);
    }
  }
  return cells;
}

function isMistake(puzzle: Board, index: number, value: CellValue): boolean {
  return countSolutions(boardWithEntry(puzzle, index, value), 1) === 0;
}

function derive(
  state: Omit<GameSession, 'conflicts' | 'incorrectCells' | 'canUndo' | 'canRedo'>,
): GameSession {
  const board = composeBoard(state.puzzle, state.entries);
  const completed = isComplete(board);
  const status =
    state.status === 'failed'
      ? 'failed'
      : completed
        ? 'completed'
        : state.status === 'completed'
          ? 'playing'
          : state.status;
  return {
    ...state,
    status,
    conflicts: conflictingCells(board),
    incorrectCells: incorrectCells(state.puzzle, state.entries),
    canUndo: status !== 'failed' && state.history.length > 0,
    canRedo: status !== 'failed' && state.future.length > 0,
  };
}

export function createSession(
  puzzle: Board,
  difficulty: Difficulty,
  startedAtMs: number,
): GameSession {
  return derive({
    puzzle,
    entries: boardFromValues(Array<CellValue>(BOARD_SIZE).fill(0)),
    candidates: Array<number>(BOARD_SIZE).fill(0),
    selectedIndex: 0,
    difficulty,
    status: 'playing',
    elapsedMs: 0,
    startedAtMs,
    automaticallyPaused: false,
    mistakes: 0,
    maxMistakes: MAX_MISTAKES,
    history: [],
    future: [],
  });
}

function withEdit(
  state: GameSession,
  entries: Board,
  candidates: readonly number[],
  mistakes = state.mistakes,
): GameSession {
  return derive({
    ...state,
    entries,
    candidates,
    mistakes,
    history: [...state.history, snapshot(state)],
    future: [],
  });
}

function isEditable(state: GameSession): boolean {
  return state.status === 'playing' && state.puzzle[state.selectedIndex] === 0;
}

function elapsedAt(state: GameSession, nowMs: number): number {
  return state.startedAtMs === null
    ? state.elapsedMs
    : state.elapsedMs + Math.max(0, nowMs - state.startedAtMs);
}

export function reduceSession(state: GameSession, action: SessionAction): GameSession {
  switch (action.type) {
    case 'select':
      return action.index >= 0 && action.index < BOARD_SIZE
        ? { ...state, selectedIndex: action.index }
        : state;
    case 'enterValue': {
      if (!isEditable(state) || action.value === 0) {
        return state;
      }
      const entries = [...state.entries];
      const candidates = [...state.candidates];
      entries[state.selectedIndex] = action.value;
      candidates[state.selectedIndex] = 0;
      for (const peer of peerIndexes(state.selectedIndex)) {
        candidates[peer] = (candidates[peer] ?? 0) & ~(1 << action.value);
      }
      const mistakes = isMistake(state.puzzle, state.selectedIndex, action.value)
        ? state.mistakes + 1
        : state.mistakes;
      const next = withEdit(state, boardFromValues(entries), candidates, mistakes);
      if (next.status === 'completed' && action.nowMs !== undefined) {
        return {
          ...next,
          elapsedMs: elapsedAt(state, action.nowMs),
          startedAtMs: null,
        };
      }
      if (mistakes >= state.maxMistakes) {
        return {
          ...next,
          status: 'failed',
          elapsedMs: action.nowMs === undefined ? next.elapsedMs : elapsedAt(state, action.nowMs),
          startedAtMs: null,
          canUndo: false,
          canRedo: false,
        };
      }
      return next;
    }
    case 'toggleCandidate': {
      if (!isEditable(state) || action.value === 0 || state.entries[state.selectedIndex] !== 0) {
        return state;
      }
      const candidates = [...state.candidates];
      candidates[state.selectedIndex] =
        (candidates[state.selectedIndex] ?? 0) ^ (1 << action.value);
      return withEdit(state, state.entries, candidates);
    }
    case 'clear': {
      if (!isEditable(state)) {
        return state;
      }
      const entries = [...state.entries];
      const candidates = [...state.candidates];
      if (entries[state.selectedIndex] === 0 && candidates[state.selectedIndex] === 0) {
        return state;
      }
      entries[state.selectedIndex] = 0;
      candidates[state.selectedIndex] = 0;
      return withEdit(state, boardFromValues(entries), candidates);
    }
    case 'undo': {
      if (state.status === 'failed') {
        return state;
      }
      const previous = state.history.at(-1);
      if (previous === undefined) {
        return state;
      }
      return derive({
        ...state,
        entries: previous.entries,
        candidates: previous.candidates,
        history: state.history.slice(0, -1),
        future: [snapshot(state), ...state.future],
      });
    }
    case 'redo': {
      if (state.status === 'failed') {
        return state;
      }
      const next = state.future[0];
      if (next === undefined) {
        return state;
      }
      return derive({
        ...state,
        entries: next.entries,
        candidates: next.candidates,
        history: [...state.history, snapshot(state)],
        future: state.future.slice(1),
      });
    }
    case 'pause':
      if (state.status !== 'playing') {
        return state;
      }
      return {
        ...state,
        elapsedMs: elapsedAt(state, action.nowMs),
        startedAtMs: null,
        status: 'paused',
        automaticallyPaused: action.automatic,
      };
    case 'resume':
      return state.status === 'paused'
        ? {
            ...state,
            status: 'playing',
            startedAtMs: action.nowMs,
            automaticallyPaused: false,
          }
        : state;
    case 'resumeAutomatic':
      return state.status === 'paused' && state.automaticallyPaused
        ? {
            ...state,
            status: 'playing',
            startedAtMs: action.nowMs,
            automaticallyPaused: false,
          }
        : state;
    case 'restart':
      return createSession(state.puzzle, state.difficulty, action.nowMs);
  }
}

export function displayedElapsedMs(state: GameSession, nowMs: number): number {
  return elapsedAt(state, nowMs);
}

export function restoreSession(
  values: Pick<
    GameSession,
    'puzzle' | 'entries' | 'candidates' | 'selectedIndex' | 'difficulty' | 'elapsedMs' | 'mistakes'
  >,
  status: Extract<SessionStatus, 'paused' | 'failed'> = 'paused',
): GameSession {
  return derive({
    ...values,
    status,
    startedAtMs: null,
    automaticallyPaused: false,
    maxMistakes: MAX_MISTAKES,
    history: [],
    future: [],
  });
}

export function restoreLegacySession(
  values: Pick<
    GameSession,
    'puzzle' | 'entries' | 'candidates' | 'selectedIndex' | 'difficulty' | 'elapsedMs'
  >,
): GameSession {
  return restoreSession({ ...values, mistakes: 0 });
}
